import { styled } from "../../../styles";

/** Label/value pairs of the session detail, above the raw TMP JSON blocks. */
export const DetailGrid = styled("div", {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
  gap: "12px 24px",
});

export const DetailSectionTitle = styled("h3", {
  margin: "8px 0 0",
  fontSize: "14px",
  fontWeight: "600",
  color: "$charcoal",
  fontFamily: "$openSans",
});

/** The freshly minted completion link, with its copy control. */
export const LinkRow = styled("div", {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "12px",
  borderRadius: "6px",
  backgroundColor: "$success-lightest",
});

export const LinkValue = styled("code", {
  flex: 1,
  fontSize: "12px",
  fontFamily: "monospace",
  color: "$charcoal",
  wordBreak: "break-all",
});

export const CopyLinkButton = styled("button", {
  height: "32px",
  padding: "0 12px",
  borderRadius: "6px",
  border: "1px solid $gray-40",
  backgroundColor: "$white",
  color: "$charcoal",
  fontSize: "13px",
  fontFamily: "$openSans",
  cursor: "pointer",
  whiteSpace: "nowrap",

  "&:hover": {
    backgroundColor: "$gray-20",
  },

  variants: {
    copied: {
      true: {
        borderColor: "$success",
        color: "$success-dark",
      },
    },
  },
});

/** A long id, cut short in the table; the full value is in the title. */
export const IdCell = styled("span", {
  display: "block",
  maxWidth: "140px",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: "13px",
  color: "$gray-90",
});

/** Status line of the drill-down: badge, then what happens next. */
export const StatusSummary = styled("div", {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "8px 12px",
  padding: "12px 16px",
  borderRadius: "8px",
  border: "1px solid $gray-40",
  backgroundColor: "$gray-20",
});

export const NextStepText = styled("span", {
  fontSize: "14px",
  fontFamily: "$openSans",
  color: "$charcoal",

  variants: {
    tone: {
      active: { color: "$success-dark" },
      inactive: { color: "$charcoal" },
      warning: { color: "$charcoal" },
      error: { color: "$error-dark" },
    },
  },
});

/** Timeline of the order's history, oldest first. */
export const Timeline = styled("ol", {
  listStyle: "none",
  margin: 0,
  padding: 0,
  display: "flex",
  flexDirection: "column",
});

export const TimelineItem = styled("li", {
  position: "relative",
  display: "grid",
  gridTemplateColumns: "16px 1fr",
  columnGap: "12px",
  paddingBottom: "14px",

  // The connector between two dots.
  "&:not(:last-child)::before": {
    content: '""',
    position: "absolute",
    left: "7px",
    top: "16px",
    bottom: 0,
    width: "2px",
    backgroundColor: "$gray-40",
  },
});

export const TimelineDot = styled("span", {
  width: "12px",
  height: "12px",
  marginTop: "4px",
  marginLeft: "2px",
  borderRadius: "50%",
  border: "2px solid $white",

  variants: {
    tone: {
      active: { backgroundColor: "$success" },
      inactive: { backgroundColor: "$gray-70" },
      warning: { backgroundColor: "$warning" },
      error: { backgroundColor: "$error" },
    },
  },
});

export const TimelineBody = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  minWidth: 0,
});

export const TimelineHeading = styled("div", {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "baseline",
  gap: "4px 10px",
  fontFamily: "$openSans",
});

export const TimelineTitle = styled("span", {
  fontSize: "14px",
  fontWeight: "600",
  color: "$charcoal",
});

export const TimelineMeta = styled("span", {
  fontSize: "12px",
  color: "$gray-90",
});

export const TimelineDetail = styled("span", {
  fontSize: "13px",
  color: "$gray-90",
  fontFamily: "$openSans",
  wordBreak: "break-word",
});

export const TimelineNote = styled("p", {
  margin: 0,
  fontSize: "12px",
  color: "$gray-90",
  fontFamily: "$openSans",
});

/** A collapsible block (patient data, technical ids). */
export const Collapsible = styled("details", {
  borderTop: "1px solid $gray-40",
  paddingTop: "12px",

  "& > summary": {
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
    color: "$charcoal",
    fontFamily: "$openSans",
    marginBottom: "12px",
  },

  "& > summary:focus-visible": {
    outline: "2px solid $primary",
    outlineOffset: "2px",
  },
});

/** Two lines in one table cell: the value and a muted caption under it. */
export const CellStack = styled("span", {
  display: "flex",
  flexDirection: "column",
  gap: "2px",
});

export const CellCaption = styled("span", {
  fontSize: "12px",
  color: "$gray-90",
});

export const NextStepCell = styled("span", {
  display: "block",
  maxWidth: "280px",
  fontSize: "13px",
  whiteSpace: "normal",

  variants: {
    tone: {
      active: { color: "$gray-90" },
      inactive: { color: "$gray-90" },
      warning: { color: "$warning-darkest" },
      error: { color: "$error-dark" },
    },
  },
});
